import Grid from "@mui/material/Grid";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import AssignmentTemplateSummaryCard from "./AssignmentTemplateSummaryCard";

export const FruitifyAssignmentTemplateSearch = ({
  assignments,
  showAnswerCount = false,
  viewTemplateSearchParams = null,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [subject, setSubject] = useState("");

  const assignmentLevels = useMemo(() => {
    const levelsSet = new Set(assignments.map((a) => a.educationLevel.level));
    return Array.from(levelsSet);
  }, [assignments]);

  const assignmentSubjects = useMemo(() => {
    const subjectsSet = new Set(
      assignments.flatMap((a) => a.subjects.map((s) => s.name)),
    );
    return Array.from(subjectsSet);
  }, [assignments]);

  const getDerivedStatus = (a) => {
    if (a.status) return a.status;
    if (!a.dueDate) return "";
    try {
      const due = new Date(a.dueDate);
      const now = new Date();
      return due.getTime() >= now.getTime() ? "Upcoming" : "Past Deadline";
    } catch {
      return "";
    }
  };

  const filteredAssignments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return assignments
      .filter((a) =>
        educationLevel ? a.educationLevel?.level === educationLevel : true,
      )
      .filter((a) =>
        subject
          ? a.subjects.some(
              (s) => s.name === subject || s.parent?.name === subject,
            )
          : true,
      )
      .filter((a) => {
        if (!term) return true;
        const subjectNames = a.subjects.flatMap((s) =>
          [s.name, s.parent?.name].filter(Boolean),
        );
        const tagStrings = Array.isArray(a.tags)
          ? a.tags.map((t) => (typeof t === "string" ? t : t.tag || ""))
          : [];
        const haystacks = [
          a.schoolClass?.name || "",
          getDerivedStatus(a) || a.status || "",
          ...tagStrings,
          ...subjectNames,
        ];
        return haystacks.some((h) => h.toLowerCase().includes(term));
      });
  }, [assignments, searchTerm, educationLevel, subject]);

  return (
    <Grid
      container
      spacing={2}
      sx={{ width: "100%", mt: 6 }}
      data-testid={"fruitify-assignment-template-search"}
    >
      <Grid size={12} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: "100%" }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 280 } }}
          />
          <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
            <InputLabel id="education-level-label">Education Level</InputLabel>
            <Select
              labelId="education-level-label"
              value={educationLevel}
              label="EducationLevel"
              onChange={(e) => setEducationLevel(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {assignmentLevels.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
            <InputLabel id="subjects-label">Subject</InputLabel>
            <Select
              labelId="subjects-label"
              value={subject}
              label="Subject"
              onChange={(e) => setSubject(e.target.value)}
            >
              {assignmentSubjects.map((subj) => (
                <MenuItem key={subj} value={subj}>
                  {subj}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Grid>
      {filteredAssignments.map((assignmentTemplate, index) => (
        <Grid size={{ xs: 12, md: 4 }} key={index} sx={{ display: "flex" }}>
          <AssignmentTemplateSummaryCard
            assignmentTemplate={assignmentTemplate}
            showAnswerCount={showAnswerCount}
            viewTemplateSearchParams={viewTemplateSearchParams}
          />
        </Grid>
      ))}
    </Grid>
  );
};
